import { scrubDeep, tracker } from './error-tracking'

/**
 * Product telemetry (frontend phase-14 §2.4).
 *
 * **A small, purposeful event set that answers the rollout's questions and nothing more.** The
 * list below is closed and typed: `track('some_idea')` is a compile error, not a new event. An
 * event nobody can name a question for is a privacy liability somebody will eventually have to
 * defend, and every event here is documented in `docs/v2/telemetry.md` with its question.
 *
 * Four rules, enforced by this module rather than by discipline:
 *
 * 1. **Do Not Track is respected.** `navigator.doNotTrack === '1'` (and the older
 *    `window.doNotTrack` / `navigator.msDoNotTrack`) turns the whole thing off. Not "we
 *    anonymise more"; off.
 * 2. **No PII, ever.** Properties are whitelisted per event and every value is passed through
 *    the Phase 13 scrubber on the way out. There is no free-text property anywhere in the
 *    schema, so there is nothing for a name or an email to be carried in.
 * 3. **No money.** Amounts are never sent. `payment_completed` records that a payment
 *    completed; what it was worth is in the database, where it is already correct.
 * 4. **Ids are the platform's own.** A property id or a listing slug is fine — it identifies a
 *    thing, not a person. A user id travels only as the error tracker's `user.id`, never as an
 *    event property.
 *
 * The sink is pluggable and **defaults to nothing**: without `VITE_ANALYTICS_ENDPOINT` events
 * are dropped (development, tests, and any deployment that has not chosen a provider). That is
 * the honest default for a module that exists to send data somewhere.
 */

/** The closed event set. Adding a row here means adding a row to `docs/v2/telemetry.md`. */
export const EVENTS = {
  // --- Rollout question: are people getting in, and by which door? -----------------------
  SIGN_IN_SUCCEEDED: 'sign_in_succeeded',
  SIGN_IN_FAILED: 'sign_in_failed',
  WORKSPACE_ENTERED: 'workspace_entered',
  // --- Rollout question: does onboarding actually complete? ------------------------------
  ONBOARDING_STEP_REACHED: 'onboarding_step_reached',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_ABANDONED: 'onboarding_abandoned',
  AFFILIATE_LANDLORD_ONBOARDED: 'affiliate_landlord_onboarded',
  // --- Rollout question: does the marketplace convert as well as V1's site? --------------
  MARKETPLACE_SEARCH_PERFORMED: 'marketplace_search_performed',
  LISTING_VIEWED: 'listing_viewed',
  // --- Rollout question: does checkout finish, and where does it stop? -------------------
  CHECKOUT_STEP_REACHED: 'checkout_step_reached',
  PAYMENT_INITIATED: 'payment_initiated',
  PAYMENT_COMPLETED: 'payment_completed',
  PAYMENT_FAILED: 'payment_failed',
  // --- Rollout question: is the tenancy work being done in V2? ---------------------------
  RENEWAL_RECORDED: 'renewal_recorded',
  // --- Rollout question: is anyone leaving, and what does the way back cost us? ----------
  PORTAL_SWITCHED: 'portal_switched',
} as const

export type EventName = (typeof EVENTS)[keyof typeof EVENTS]

/**
 * The properties each event may carry. Anything not listed is dropped before sending, so a
 * `track` call that grows an extra property does not quietly start exporting it.
 */
const ALLOWED_PROPERTIES: Record<EventName, readonly string[]> = {
  [EVENTS.SIGN_IN_SUCCEEDED]: ['method'],
  [EVENTS.SIGN_IN_FAILED]: ['method', 'reason_code'],
  [EVENTS.WORKSPACE_ENTERED]: ['workspace'],
  [EVENTS.ONBOARDING_STEP_REACHED]: ['step', 'flow'],
  [EVENTS.ONBOARDING_COMPLETED]: ['flow', 'spaces_created'],
  [EVENTS.ONBOARDING_ABANDONED]: ['step', 'flow'],
  [EVENTS.AFFILIATE_LANDLORD_ONBOARDED]: ['had_property'],
  [EVENTS.MARKETPLACE_SEARCH_PERFORMED]: ['has_city', 'has_price_filter', 'result_bucket'],
  [EVENTS.LISTING_VIEWED]: ['listing_slug', 'source'],
  [EVENTS.CHECKOUT_STEP_REACHED]: ['step'],
  [EVENTS.PAYMENT_INITIATED]: ['channel'],
  [EVENTS.PAYMENT_COMPLETED]: ['channel'],
  [EVENTS.PAYMENT_FAILED]: ['channel', 'reason_code'],
  [EVENTS.RENEWAL_RECORDED]: ['channel'],
  [EVENTS.PORTAL_SWITCHED]: ['to', 'source'],
}

export type EventProperties = Record<string, string | number | boolean | null | undefined>

export interface TelemetrySink {
  send(name: EventName, properties: EventProperties): void
}

/** The default. Sends nothing, on purpose. */
class NullSink implements TelemetrySink {
  send() {}
}

/**
 * `navigator.sendBeacon` where it exists, so an event fired during a navigation is not lost and
 * does not delay the navigation either. `keepalive` fetch is the fallback.
 */
class BeaconSink implements TelemetrySink {
  constructor(private readonly endpoint: string) {}

  send(name: EventName, properties: EventProperties) {
    const body = JSON.stringify({
      event: name,
      properties,
      release: import.meta.env.VITE_APP_RELEASE || 'dev',
      at: new Date().toISOString(),
    })
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(this.endpoint, new Blob([body], { type: 'application/json' }))
        return
      }
      void fetch(this.endpoint, {
        method: 'POST',
        body,
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
      }).catch(() => {})
    } catch {
      // Telemetry never breaks the app. An analytics endpoint that is down, blocked by an
      // extension or refused by CSP must be indistinguishable from one that is not configured.
    }
  }
}

let sink: TelemetrySink = new NullSink()
let enabled = false

/** Do Not Track, in all three spellings browsers have used. */
export function doNotTrack(): boolean {
  if (typeof navigator === 'undefined' && typeof window === 'undefined') return false
  const nav =
    typeof navigator === 'undefined'
      ? undefined
      : (navigator as Navigator & {
          msDoNotTrack?: string
        })
  const win =
    typeof window === 'undefined'
      ? undefined
      : (window as Window & {
          doNotTrack?: string
        })
  const signals = [nav?.doNotTrack, nav?.msDoNotTrack, win?.doNotTrack]
  return signals.some((value) => value === '1' || value === 'yes')
}

/**
 * Turn telemetry on, if there is somewhere to send it and the viewer has not asked not to be
 * tracked. Returns whether it ended up enabled, which the tests assert on.
 */
export function initTelemetry(options: { endpoint?: string; sink?: TelemetrySink } = {}): boolean {
  const endpoint = options.endpoint ?? import.meta.env.VITE_ANALYTICS_ENDPOINT
  if (doNotTrack()) {
    enabled = false
    sink = new NullSink()
    return false
  }
  if (options.sink) {
    sink = options.sink
    enabled = true
    return true
  }
  if (!endpoint) {
    enabled = false
    sink = new NullSink()
    return false
  }
  sink = new BeaconSink(endpoint)
  enabled = true
  return true
}

/** Test seam, and the way an incident turns telemetry off without a deploy. */
export function setTelemetrySink(next: TelemetrySink | null): void {
  sink = next ?? new NullSink()
  enabled = next !== null && !doNotTrack()
}

export function telemetryEnabled(): boolean {
  return enabled
}

/**
 * Record one event.
 *
 * Never throws, never awaits, and never carries anything that was not whitelisted for this
 * event. Calling it when telemetry is off is a no-op, so call sites do not need to check.
 */
export function track(name: EventName, properties: EventProperties = {}): void {
  if (!enabled) return
  const allowed = ALLOWED_PROPERTIES[name] ?? []
  const clean: EventProperties = {}
  for (const key of allowed) {
    const value = properties[key]
    if (value === undefined) continue
    clean[key] = typeof value === 'string' ? (scrubDeep(value) as string) : value
  }
  try {
    sink.send(name, clean)
  } catch (error) {
    tracker().capture(error, { telemetryEvent: name })
  }
}

/**
 * Web vitals, reported per route (frontend phase-14 §2.4).
 *
 * Implemented straight on `PerformanceObserver` rather than by adding the `web-vitals` package:
 * LCP, CLS and INP are three observers and about forty lines, and the budgets in
 * `docs/v2/performance-report.md` are checked against these three numbers only. A dependency on
 * the public path has to earn its bytes.
 *
 * Returns a disposer. Values are reported once per page load, at the point the page is hidden —
 * which is when LCP and CLS are final.
 */
export function observeWebVitals(
  report: (metric: { name: 'LCP' | 'CLS' | 'INP'; value: number; route: string }) => void,
  routeOf: () => string,
): () => void {
  if (typeof PerformanceObserver === 'undefined' || typeof document === 'undefined') {
    return () => {}
  }

  let lcp = 0
  let cls = 0
  let inp = 0
  const observers: PerformanceObserver[] = []

  const observe = (type: string, handler: (entries: PerformanceEntryList) => void) => {
    try {
      const observer = new PerformanceObserver((list) => handler(list.getEntries()))
      // `buffered` picks up entries that happened before this ran, which for LCP is most of them.
      observer.observe({ type, buffered: true } as PerformanceObserverInit)
      observers.push(observer)
    } catch {
      // A browser without this entry type reports nothing for it. Not an error.
    }
  }

  observe('largest-contentful-paint', (entries) => {
    for (const entry of entries) lcp = Math.max(lcp, entry.startTime)
  })
  observe('layout-shift', (entries) => {
    for (const entry of entries as (PerformanceEntry & {
      value: number
      hadRecentInput: boolean
    })[]) {
      if (!entry.hadRecentInput) cls += entry.value
    }
  })
  observe('event', (entries) => {
    for (const entry of entries as (PerformanceEntry & { duration: number })[]) {
      inp = Math.max(inp, entry.duration)
    }
  })

  const flush = () => {
    if (document.visibilityState !== 'hidden') return
    const route = routeOf()
    if (lcp) report({ name: 'LCP', value: Math.round(lcp), route })
    if (cls) report({ name: 'CLS', value: Number(cls.toFixed(3)), route })
    if (inp) report({ name: 'INP', value: Math.round(inp), route })
    lcp = 0
    cls = 0
    inp = 0
  }

  document.addEventListener('visibilitychange', flush)
  return () => {
    document.removeEventListener('visibilitychange', flush)
    for (const observer of observers) observer.disconnect()
  }
}
